import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

interface PercentageChange {
  wetPercentage: number;
  dryPercentage: number;
}

@Component({
  selector: 'app-food-combination-banner',
  templateUrl: './food-combination-banner.component.html',
  styleUrl: './food-combination-banner.component.scss'
})
export class FoodCombinationBannerComponent implements OnDestroy {
  @Input() wetPercentage: number = 0;
  @Input() dryPercentage: number = 0;
  @Input() combineFoodError: boolean = false;
  @Input() showBanner: boolean = false;

  @Output() percentageChange = new EventEmitter<PercentageChange>();

  private percentageChangeSubject = new Subject<PercentageChange>();
  private destroy$ = new Subject<void>();

  constructor() {
    this.percentageChangeSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.percentageChange.emit(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getWetFoodBowlImage(): string {
    const dry = this.dryPercentage;
    const wet = this.wetPercentage;

    if (dry === 0 && wet === 0) return 'pot-combine-food-wet-0.png';
    if (dry < 10) return 'pot-combine-food-wet-10.png';
    if (dry < 20) return 'pot-combine-food-wet-10.png';
    if (dry < 30) return 'pot-combine-food-wet-20.png';
    if (dry < 40) return 'pot-combine-food-wet-30.png';
    if (dry < 50) return 'pot-combine-food-wet-40.png';
    if (dry < 60) return 'pot-combine-food-wet-50.png';
    if (dry < 70) return 'pot-combine-food-wet-60.png';
    if (dry < 80) return 'pot-combine-food-wet-70.png';
    if (dry < 90) return 'pot-combine-food-wet-80.png';
    return 'pot-combine-food-wet-90.png';
  }

  onPercentageChange(value: number, type: 'wet' | 'dry') {
    if (!isFinite(value)) value = 1;
    value = Math.trunc(value);
    if (value < 1) value = 1;
    if (value > 99) value = 99;

    let wetPercentage: number;
    let dryPercentage: number;

    if (type === 'wet') {
      wetPercentage = value;
      dryPercentage = 100 - value;
    } else {
      dryPercentage = value;
      wetPercentage = 100 - value;
    }

    this.percentageChangeSubject.next({ wetPercentage, dryPercentage });
  }

  handleKeyDown(event: KeyboardEvent) {
    if (
      ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
        event.key
      ) ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }
    if (!/^[1-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handleInput(event: Event, type: 'wet' | 'dry') {
    const inputValue = event.target as HTMLInputElement;
    const cleanedValue = inputValue.value.replace(/[^\d]/g, '');
    const numericValue = cleanedValue === '' ? NaN : Number(cleanedValue);
    const valueToUse = Number.isFinite(numericValue)
      ? Math.trunc(numericValue)
      : 1;
    const clampedValue = Math.min(99, Math.max(1, valueToUse));

    inputValue.value = String(clampedValue);

    this.onPercentageChange(clampedValue, type);
  }

  preventInvalidChars(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) return;

    const allowedKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Home',
      'End',
      'Enter'
    ];
    if (allowedKeys.includes(event.key)) return;

    if (/[eE+\-.,]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent) {
    const paste = event.clipboardData?.getData('text') ?? '';
    if (!/^\d+$/.test(paste)) {
      event.preventDefault();
      return;
    }
    const numericValue = Math.trunc(Number(paste));
    const clampedValue = Math.min(99, Math.max(1, numericValue));
    const input = event.target as HTMLInputElement;
    setTimeout(() => (input.value = String(clampedValue)), 0);
  }
}
